import React, { createContext, useContext, ReactNode } from 'react'
import { useFetchData } from '../../common/Hooks'
import Training from '../../common/types/Training'
import TrainingDTO from '../../common/types/DTOs/TrainingDTO'
import { convertToTraining } from '../layout/Utils'
import moment from 'moment'

interface TrainingSchedulesContextProps {
  scheduledTrainings: Training[]
  isLoading: boolean
  error: Error | null
  refetchSchedules: () => void
  getSchedulesForAthleteAndDay: (athleteId: number, day: Date | string) => Training[]
  getSchedulesForDateRange: (startDate: Date, endDate: Date, athleteIds?: number[]) => Training[]
}

const TrainingSchedulesContext = createContext<TrainingSchedulesContextProps>({
  scheduledTrainings: [],
  isLoading: false,
  error: null,
  refetchSchedules: () => {},
  getSchedulesForAthleteAndDay: () => [],
  getSchedulesForDateRange: () => []
})

interface TrainingSchedulesProviderProps {
  children: ReactNode
  startDate?: Date
  endDate?: Date
}

export const TrainingSchedulesProvider: React.FC<TrainingSchedulesProviderProps> = ({
  children,
  startDate = moment().subtract(7, 'year').startOf('day').toDate(),
  endDate = moment().add(1, 'year').endOf('day').toDate()
}) => {
  const fetchingParams = {
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString()
  }

  const {
    data: schedules = [] as TrainingDTO[],
    fetched: isLoading,
    reFetch: refetchSchedules
  } = useFetchData('/api/v1/schedules', fetchingParams)

  const scheduledTrainings: Training[] = schedules.map(schedule => convertToTraining(schedule))

  // Helper function to check if training is for specific athlete(s)
  const isTrainingForAthletes = (training: Training, athleteIds: number[]): boolean => {
    return training.athletes?.profiles?.some(profile => athleteIds.includes(profile.id)) || false
  }

  const getSchedulesForAthleteAndDay = (athleteId: number, day: Date | string): Training[] => {
    const dayAsDate = typeof day === 'string' ? new Date(day) : day
    return getSchedulesForDateRange(dayAsDate, dayAsDate, [athleteId])
  }

  const getSchedulesForDateRange = (startDate: Date, endDate: Date, athleteIds?: number[]): Training[] => {
    const start = moment(startDate).startOf('day')
    const end = moment(endDate).endOf('day')

    return scheduledTrainings.filter(training => {
      const trainingDate = moment(training.start_date)
      const isInRange = trainingDate.isBetween(start, end, 'day', '[]')

      if ((athleteIds == null) || athleteIds.length === 0) {
        return isInRange
      }

      return isInRange && isTrainingForAthletes(training, athleteIds)
    })
  }

  const contextValue: TrainingSchedulesContextProps = {
    scheduledTrainings,
    isLoading: !isLoading, // fetched is true when loaded, so we invert it
    error: null, // useFetchData doesn't provide error in this implementation
    refetchSchedules,
    getSchedulesForAthleteAndDay,
    getSchedulesForDateRange
  }

  return (
    <TrainingSchedulesContext.Provider value={contextValue}>
      {children}
    </TrainingSchedulesContext.Provider>
  )
}

export const useTrainingSchedules = (): TrainingSchedulesContextProps => {
  const context = useContext(TrainingSchedulesContext)
  return context
}

// Helper function to safely get schedules with fallback
export const useSchedulesForAthleteAndDay = (athleteId: number, day: Date | string): Training[] => {
  const { getSchedulesForAthleteAndDay } = useTrainingSchedules()
  return getSchedulesForAthleteAndDay(athleteId, day)
}
